import { Alert, Box, Button, Paper, SvgIcon, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { env, getAuthUrl } from '../config/env';
import { useAuth } from '../contexts/AuthContext';

// Get configuration from validated env schema
const SSO_ENABLED = env.VITE_SSO_ENABLED;

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const [error, setError] = useState('');

  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    const ssoError = searchParams.get('error');
    if (ssoError) {
      setError(
        ssoError === 'auth_failed'
          ? 'SSO authentication failed. Please try again.'
          : `Authentication error: ${ssoError}`
      );
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from === '/' ? '/top-level-kpi' : from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSSOLogin = () => {
    const authLoginUrl = getAuthUrl('/auth/login');

    window.location.href = authLoginUrl;
  };

  return (
    <Box
      display='flex'
      justifyContent='center'
      alignItems='center'
      minHeight='100vh'
      sx={{ backgroundColor: 'background.default', p: 2 }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant='h4' component='h1' gutterBottom textAlign='center'>
          Sign In
        </Typography>
        <Typography
          variant='body1'
          color='text.secondary'
          textAlign='center'
          paragraph
        >
          Access your delivery metrics dashboard
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {SSO_ENABLED && (
          <Button
            fullWidth
            variant='contained'
            onClick={handleSSOLogin}
            sx={{
              mt: 1,
              mb: 2,
              py: 1.5,
              backgroundColor: '#0078d4',
              '&:hover': { backgroundColor: '#106ebe' },
              textTransform: 'none',
              fontSize: '1rem',
            }}
            aria-label='Sign in with Microsoft Entra ID'
            startIcon={<MicrosoftIcon />}
          >
            Sign in with Microsoft
          </Button>
        )}

        {!SSO_ENABLED && (
          <Alert severity='warning' sx={{ mt: 2 }}>
            SSO is not enabled. Please contact your administrator.
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

// Microsoft logo icon component
const MicrosoftIcon: React.FC = () => (
  <SvgIcon sx={{ fontSize: 20 }}>
    <svg viewBox='0 0 21 21' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0' y='0' width='10' height='10' fill='#f25022' />
      <rect x='11' y='0' width='10' height='10' fill='#7fba00' />
      <rect x='0' y='11' width='10' height='10' fill='#00a4ef' />
      <rect x='11' y='11' width='10' height='10' fill='#ffb900' />
    </svg>
  </SvgIcon>
);
